/**
 * WHERE EACH REQUIREMENT IS DONE.
 *
 * A row that says "Email domain registered ✕" and offers nothing leaves the
 * reader to hunt for the control — and for domains it was folded away inside
 * the single sign-on panel. Every row that is ours to do now names the place:
 * a button that opens the stage, opens the editor if one is needed, and puts
 * the cursor in the field (or presses the button that starts it).
 *
 * Pure, and table-tested. Met and not-required rows get nothing.
 */
import { ROLE_LABEL } from './messageFor';
import type { Requirement, StageId } from './types';

export interface Jump {
  stage: StageId;
  /** Element id to bring into view and focus. */
  target: string;
  /** Press it instead of focusing it: for a button that opens a form. */
  click?: boolean;
  /** Something the stage must open first: "editIntake", "newContact:<role>". */
  prepare?: string;
}

export type RequirementAction =
  | { label: string; jump: Jump }
  | { label: string; href: string };

const INTAKE_FIELDS: Record<string, string> = {
  legalName: 'organization-legalName',
  address: 'organization-address',
  locationCount: 'organization-locationCount',
  organizationType: 'organization-organizationType',
  targetGoLiveDate: 'organization-targetGoLiveDate',
  poNumber: 'requirements-poNumber',
};

const editIntake = (field: string): RequirementAction => ({
  label: 'Enter it',
  jump: { stage: 'organization', prepare: 'editIntake', target: `ie-${INTAKE_FIELDS[field]}` },
});

const newContact = (role: string, label?: string): RequirementAction => ({
  label: label ?? `Add the ${(ROLE_LABEL[role] ?? role).toLowerCase()} contact`,
  jump: { stage: 'contacts', prepare: `newContact:${role}`, target: 'person-name' },
});

export function actionFor(r: Requirement, orgId: string): RequirementAction | null {
  if (r.status === 'met' || r.status === 'notRequired') return null;
  const key = r.key;

  if (key === 'intakeReturned') {
    return { label: 'Send the form or enter the answers', jump: { stage: 'organization', target: 'intake-to' } };
  }
  if (key in INTAKE_FIELDS) return editIntake(key);
  if (key === 'domain' || key === 'domainForRouting') {
    return { label: 'Add a domain', jump: { stage: 'organization', target: 'org-domain-new' } };
  }
  if (key === 'verifiedDomain') {
    return { label: 'Verify a domain', jump: { stage: 'organization', target: 'org-domains-title' } };
  }
  if (key.startsWith('contact.')) return newContact(key.slice('contact.'.length));
  if (key === 'billingContact') return newContact('billing', 'Add a billing contact');
  if (key.startsWith('paper.')) {
    return { label: 'Set its status', jump: { stage: 'agreements', target: `paper-${key.slice('paper.'.length)}` } };
  }
  if (key === 'contract') {
    return { label: 'Record the contract', jump: { stage: 'billing', target: 'billing-record-contract', click: true } };
  }
  if (key === 'sysAdminNamed') {
    return { label: 'Name one', jump: { stage: 'administrators', target: 'sysadmin-email' } };
  }
  if (key === 'sysAdminSignedIn') {
    return { label: 'Resend the invitation', jump: { stage: 'administrators', target: 'sysadmin-resend' } };
  }
  if (key === 'reminders') {
    return { label: 'Turn it on', jump: { stage: 'configuration', target: 'reminders-toggle' } };
  }
  if (key === 'systemSender') return { label: 'Set up email', href: '/console/system-email' };
  if (key === 'policies') return { label: 'Import their library', href: `/console/orgs/${orgId}/import` };
  // Theirs to do, or set up elsewhere entirely: DocuSign, sign-in method,
  // single sign-on registration, Google Authenticator.
  return null;
}
